/** @format */

import { Injectable, inject } from '@angular/core';
import { Logger, Scene7Service } from '@shared/data-access/services';

import { CertonaSchemeItem } from '@shared/data-access/interfaces';
import { HmfProductData } from '@dsg-tech/homefield-angular';

@Injectable({
  providedIn: 'root',
})
export class RecommendationDataService {
  private readonly scene7Service = inject(Scene7Service);
  private readonly logger = inject(Logger);

  mapSchemaItemToProductCardData(
    item?: CertonaSchemeItem | null,
    imageSize = 500,
    isLCPImage = false,
  ): HmfProductData | null {
    if (!item) return null;

    return {
      assetSeoUrl: item.URL,
      ecode: item.ID,
      imageData: {
        isLCPImage,
        isNgOptimizedImageSupported: isLCPImage,
        imageUrl: this.scene7Service.setImageWidthAndHeight(item.ImageURL, imageSize, imageSize),
      },
      pricing: {
        prices: {
          maxListPrice: this.stripExtraDecimals(item.maxlistprice ?? ''),
          maxOfferPrice: this.stripExtraDecimals(item.maxofferprice),
          minListPrice: this.stripExtraDecimals(item.minlistprice ?? ''),
          minOfferPrice: this.stripExtraDecimals(item.minofferprice),
          maxMapPrice: this.stripExtraDecimals(item.maxmapprice),
        },
        isSeePriceInCart: this.isSeePriceInCart(item),
      },
      productName: item.Name,
    };
  }

  stripExtraDecimals(price: string | number): string {
    let priceStr: string;
    // Convert any number into string to be processed consistently
    if (typeof price === 'number') {
      priceStr = price.toFixed(2);
    } else {
      priceStr = price;
    }
    // Check if the string contains a decimal point
    if (priceStr.includes('.')) {
      // Split the string into the whole part and the decimal part
      const parts = priceStr.split('.');

      // If there are more than two decimal places, keep only two
      if (parts[1].length > 2) {
        return `${parts[0]}.${parts[1].substring(0, 2)}`;
      }
    }

    return priceStr;
  }

  getAnalyticsDataFromUrl(
    url: string,
  ): Record<string, never> | { recid: string | null; recToken?: string | null } {
    if (!url) return {};

    try {
      const urlObj = new URL(url);
      const recid = urlObj.searchParams.get('recid');
      const recToken = urlObj.searchParams.get('recToken');
      return {
        recid,
        recToken,
      };
    } catch (err) {
      this.logger.error('Getting analytics query params from recommendation URL failed:', err);
      return {};
    }
  }

  comparablePrice(price: string | number | undefined) {
    if (price === '' || price === undefined) {
      return 0;
    }

    if (typeof price === 'number') {
      return price;
    }

    return Number.parseFloat(price);
  }

  isSeePriceInCart(item: CertonaSchemeItem): boolean {
    const minofferprice = this.comparablePrice(item.minofferprice);
    const maxmapprice = this.comparablePrice(item.maxmapprice);

    return minofferprice < maxmapprice || item.mapPriceIndicator === 1;
  }
}
